import { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Card, List, ListItem, ListItemButton,
  ListItemAvatar, ListItemText, Avatar, Divider, TextField, Button, Alert, Dialog,
  DialogTitle, DialogContent, DialogActions, Chip, Snackbar, LinearProgress, Skeleton,
  useMediaQuery, useTheme,
} from '@mui/material';
import { Send, Delete, AttachFile, ArrowBack } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import AttachmentsPanel, { uploadAttachment } from '../components/AttachmentsPanel';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => {
  const u = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token') || u?.token || u?.authToken || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function ParentMessaging() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [messages, setMessages] = useState([]);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('inbox'); // inbox | sent | archived
  const [composeDlg, setComposeDlg] = useState(false);
  const [composeData, setComposeData] = useState({ to: '', subject: '', body: '' });
  const [composeFile, setComposeFile] = useState(null);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: composeDlg, value: composeData, onClose: () => setComposeDlg(false) });
  const [replyFile, setReplyFile] = useState(null);
  const [snack, setSnack] = useState(null); // { severity, text }

  // Send the message first, then hang the file on it (refType Message)
  // Now handled inline in sendReply/sendCompose for better UX

  useEffect(() => {
    setSelectedMsg(null);
    loadMessages();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const [subjectStats, setSubjectStats] = useState({});

  const loadMessages = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/messages?box=${tab}`, { headers: hdrs() });
      const msgs = res.data || [];
      setMessages(msgs);

      // Build subject-level stats: count and latest date
      const map = {};
      msgs.forEach(m => {
        const key = m.subject || m.threadId || 'untitled';
        if (!map[key]) map[key] = { count: 0, last: null };
        map[key].count += 1;
        const d = new Date(m.date || m.createdAt || Date.now());
        if (!map[key].last || d > map[key].last) map[key].last = d;
      });
      setSubjectStats(map);
    } catch (e) {
      console.error('Error:', e);
    } finally {
      setLoading(false);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    try {
      const r = await axios.post(`${API}/api/messages/reply`, {
        parentMessageId: selectedMsg._id,
        body: replyText,
      }, { headers: hdrs() });
      
      const messageId = r.data?._id;
      setReplyText('');
      setReplyFile(null);
      
      // Show success immediately regardless of attachment
      setSnack({ severity: 'success', text: 'Reply sent ✓' });
      
      // Attach file if provided
      if (replyFile && messageId) {
        try {
          await uploadAttachment(replyFile, 'Message', messageId);
        } catch (err) {
          setSnack({ severity: 'warning', text: 'Reply sent, but attachment failed to upload' });
        }
      }
      
      loadMessages();
      setSelectedMsg(null);
    } catch (e) {
      setSnack({ severity: 'error', text: e.response?.data?.error || 'Failed to send reply' });
    }
  };

  const sendCompose = async () => {
    if (!composeData.to || !composeData.body) return;
    try {
      const r = await axios.post(`${API}/api/messages`, composeData, { headers: hdrs() });
      const messageId = r.data?._id;
      
      setComposeData({ to: '', subject: '', body: '' });
      setComposeFile(null);
      setComposeDlg(false);
      
      // Show success immediately regardless of attachment
      setSnack({ severity: 'success', text: 'Message sent ✓' });
      
      // Attach file if provided
      if (composeFile && messageId) {
        try {
          await uploadAttachment(composeFile, 'Message', messageId);
        } catch (err) {
          setSnack({ severity: 'warning', text: 'Message sent, but attachment failed to upload' });
        }
      }
      
      loadMessages();
      setTab('sent'); // Show sent folder to confirm
    } catch (e) {
      setSnack({ severity: 'error', text: e.response?.data?.error || 'Failed to send message' });
    }
  };

  // Open a message; mark inbox mail as read (updates the unread badge)
  const openMessage = (msg) => {
    setSelectedMsg(msg);
    if (tab === 'inbox' && !msg.read) {
      axios.patch(`${API}/api/messages/${msg._id}/read`, {}, { headers: hdrs() }).catch(() => {});
      setMessages(ms => ms.map(m => m._id === msg._id ? { ...m, read: true } : m));
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${API}/api/messages/${id}`, { headers: hdrs() });
      loadMessages();
      setSelectedMsg(null);
      setSnack({ severity: 'success', text: 'Message deleted' });
    } catch (e) {
      setSnack({ severity: 'error', text: e.response?.data?.error || 'Failed to delete' });
    }
  };

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}>
      <Box py={{ xs: 2, sm: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
          <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>💬 Messages</Typography>
          <Button variant="contained" onClick={() => setComposeDlg(true)} size="small" sx={{ py: { xs: 0.6, sm: 1 }, px: { xs: 1, sm: 2 } }}>
            New Message
          </Button>
        </Box>

        <Box display="flex" gap={1} mb={1.5} sx={{ overflowX: 'auto', pb: 1, WebkitOverflowScrolling: 'touch' }}>
          {[
            { label: 'Inbox', val: 'inbox' },
            { label: 'Sent', val: 'sent' },
            { label: 'Archived', val: 'archived' },
          ].map(t => (
            <Chip
              key={t.val}
              label={t.label}
              onClick={() => setTab(t.val)}
              color={tab === t.val ? 'primary' : 'default'}
              variant={tab === t.val ? 'filled' : 'outlined'}
              sx={{ flexShrink: 0 }}
            />
          ))}
        </Box>

        {/* Slim progress bar — the page stays in place while a box loads */}
        <Box sx={{ height: 4, mb: 1.5 }}>
          {loading && <LinearProgress sx={{ borderRadius: 2 }} />}
        </Box>

        {/* Messages List and Detail View */}
        <Box display="flex" gap={2} sx={{ minHeight: { xs: 400, sm: 600 }, flexDirection: { xs: 'column', sm: 'row' } }}>
          {/* List */}
          <Card sx={{ flex: { xs: 0, sm: 1 }, width: { xs: '100%', sm: 'auto' }, maxWidth: { xs: '100%', sm: 350 }, boxShadow: 1, borderRadius: 2, overflow: 'hidden', display: { xs: selectedMsg ? 'none' : 'block', sm: 'block' } }}>
            <List sx={{ p: 0, opacity: loading ? 0.55 : 1, transition: 'opacity 0.2s', maxHeight: { xs: 400, sm: 'auto' }, overflow: { xs: 'auto', sm: 'visible' } }}>
              {loading && messages.length === 0 ? (
                // First load of this box — skeleton rows instead of a blank screen
                [...Array(4)].map((_, i) => (
                  <Box key={i} display="flex" alignItems="center" gap={2} px={2} py={2}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box flex={1}>
                      <Skeleton width="60%" />
                      <Skeleton width="85%" height={14} />
                    </Box>
                  </Box>
                ))
              ) : messages.length === 0 ? (
                <ListItem>
                  <Typography color="text.secondary" textAlign="center" width="100%" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    No messages
                  </Typography>
                </ListItem>
              ) : (
                messages.map((msg, i) => {
                  const unread = tab === 'inbox' && !msg.read;
                  return (
                    <Box key={msg._id || i}>
                      <ListItemButton
                        selected={selectedMsg?._id === msg._id}
                        onClick={() => openMessage(msg)}
                        sx={{ py: 2, bgcolor: unread ? 'primary.50' : undefined }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: unread ? 'primary.main' : 'grey.400', fontWeight: 700, width: { xs: 36, sm: 40 }, height: { xs: 36, sm: 40 } }}>
                            {(msg.fromName || msg.from || '?').charAt(0).toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={msg.fromName || msg.from || 'Unknown'}
                          secondary={msg.subject}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: unread ? 800 : 600, sx: { fontSize: { xs: '0.875rem', sm: '1rem' } } }}
                          secondaryTypographyProps={{ variant: 'caption', noWrap: true, fontWeight: unread ? 700 : 400, sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}
                        />
                        {/* Right-side: time and thread count */}
                        {(() => {
                          const key = msg.subject || msg.threadId || 'untitled';
                          const s = subjectStats[key] || {};
                          const time = s.last ? s.last.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : (msg.date || msg.createdAt) ? new Date(msg.date || msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                          return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 1, minWidth: 64 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>{time}</Typography>
                              <Chip label={s.count || 1} size="small" sx={{ mt: 0.5, fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />
                            </Box>
                          );
                        })()}
                        {unread && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', ml: 1, flexShrink: 0 }} />}
                      </ListItemButton>
                      <Divider />
                    </Box>
                  );
                })
              )}
            </List>
          </Card>

          {/* Detail View */}
          <Card sx={{ flex: 1, p: { xs: 2, sm: 3 }, boxShadow: 1, borderRadius: 2, overflow: 'auto', width: { xs: '100%', sm: 'auto' }, display: { xs: selectedMsg ? 'block' : 'none', sm: 'block' } }}>
            {!selectedMsg ? (
              <Box textAlign="center" py={8}>
                <Typography color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>Select a message to view</Typography>
              </Box>
            ) : (
              <>
                {/* Back to list — mobile only (email-style full-screen reading view) */}
                <Button
                  onClick={() => setSelectedMsg(null)}
                  startIcon={<ArrowBack />}
                  size="small"
                  sx={{ display: { xs: 'inline-flex', sm: 'none' }, mb: 1, textTransform: 'none' }}
                >
                  Back to messages
                </Button>
                <Box mb={3} pb={2} borderBottom="1px solid #eee">
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1}>
                    <Box flex={1}>
                      <Typography variant="h6" fontWeight={700} mb={1} sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, wordBreak: 'break-word' }}>
                        {selectedMsg.subject}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: 'block' }}>
                        <strong>{selectedMsg.fromName || selectedMsg.from}</strong>
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5 }}>
                        {new Date(selectedMsg.date || selectedMsg.createdAt).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, fontWeight: 600 }}>
                        {new Date(selectedMsg.date || selectedMsg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Typography variant="body2" mb={2} sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {selectedMsg.body}
                </Typography>

                {/* Files attached to this message */}
                <Box mb={3}>
                  <AttachmentsPanel refType="Message" refId={selectedMsg._id} canUpload={false} />
                </Box>

                {/* Reply Section — available on inbox and sent messages */}
                {(tab === 'inbox' || tab === 'sent') && (
                  <Box>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" fontWeight={700} mb={2} sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                      {tab === 'sent' ? 'Forward' : 'Reply'}
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder={tab === 'sent' ? 'Forward to someone else...' : 'Type your reply...'}
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      size="small"
                      sx={{ mb: 2 }}
                    />
                    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                      <Button
                        variant="contained"
                        startIcon={<Send />}
                        onClick={sendReply}
                        disabled={!replyText.trim()}
                        size="small"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        {tab === 'sent' ? 'Forward' : 'Send Reply'}
                      </Button>
                      <Button component="label" startIcon={<AttachFile />} size="small" sx={{ textTransform: 'none', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        Attach
                        <input type="file" hidden onChange={e => setReplyFile(e.target.files?.[0] || null)} />
                      </Button>
                      {replyFile && (
                        <Chip label={replyFile.name} size="small" onDelete={() => setReplyFile(null)} sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }} />
                      )}
                      <Button
                        onClick={() => deleteMessage(selectedMsg._id)}
                        startIcon={<Delete />}
                        size="small"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Card>
        </Box>

        {/* Compose Dialog */}
        {discardDialog}
        <Dialog open={composeDlg} onClose={attemptClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
          <DialogTitle>New Message</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="To (teacher/staff email)"
                value={composeData.to}
                onChange={e => setComposeData({ ...composeData, to: e.target.value })}
              />
              <TextField
                fullWidth
                size="small"
                label="Subject"
                value={composeData.subject}
                onChange={e => setComposeData({ ...composeData, subject: e.target.value })}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Message"
                value={composeData.body}
                onChange={e => setComposeData({ ...composeData, body: e.target.value })}
              />
              <Box display="flex" alignItems="center" gap={1}>
                <Button component="label" size="small" startIcon={<AttachFile />} sx={{ textTransform: 'none' }}>
                  Attach file
                  <input type="file" hidden onChange={e => setComposeFile(e.target.files?.[0] || null)} />
                </Button>
                {composeFile && (
                  <Chip label={composeFile.name} size="small" onDelete={() => setComposeFile(null)} />
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={attemptClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={sendCompose}
              disabled={!composeData.to || !composeData.body}
              startIcon={<Send />}
            >
              Send
            </Button>
          </DialogActions>
        </Dialog>

        {/* Toast */}
        <Snackbar
          open={Boolean(snack)}
          autoHideDuration={4000}
          onClose={() => setSnack(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snack?.severity || 'info'} variant="filled"
            onClose={() => setSnack(null)} sx={{ borderRadius: 2, boxShadow: 3 }}>
            {snack?.text}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
}

